/* Aria Robotics — geteilte UI-Bausteine (theme-fähig über CSS-Tokens).
 * Ported aus ui.jsx; Icons jetzt über lucide-react. */
import * as React from 'react';
import { icons, type LucideProps } from 'lucide-react';
import { res } from '@/lib/res';

type CSS = React.CSSProperties;

// ---- Icon (Lucide) ----
function toPascal(name: string): string {
  return name
    .split('-')
    .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : ''))
    .join('');
}
export interface IconProps {
  name: string;
  size?: number;
  strokeWidth?: number;
  style?: CSS;
}
const iconMap = icons as unknown as Record<string, React.FC<LucideProps>>;
// lucide renamed some icons; map our stable kebab names to current keys.
const ICON_ALIASES: Record<string, string> = {
  Home: 'House',
  PlayCircle: 'CirclePlay',
};
export function Icon({ name, size = 22, strokeWidth = 2, style }: IconProps) {
  const pascal = toPascal(name);
  const Cmp: React.FC<LucideProps> | undefined = iconMap[pascal] ?? iconMap[ICON_ALIASES[pascal] ?? ''];
  return (
    <span style={{ display: 'inline-flex', lineHeight: 0, ...style }}>
      {Cmp ? <Cmp size={size} strokeWidth={strokeWidth} /> : null}
    </span>
  );
}

// ---- Layout ----
export interface ContainerProps {
  children: React.ReactNode;
  narrow?: boolean;
  style?: CSS;
}
export function Container({ children, narrow = false, style }: ContainerProps) {
  return (
    <div style={{ width: '100%', maxWidth: narrow ? 780 : 1200, margin: '0 auto', padding: '0 24px', boxSizing: 'border-box', ...style }}>
      {children}
    </div>
  );
}

type SectionTone = 'page' | 'card' | 'sunken' | 'ink';
export interface SectionProps {
  children: React.ReactNode;
  tone?: SectionTone;
  style?: CSS;
  id?: string;
}
export function Section({ children, tone = 'page', style, id }: SectionProps) {
  const bg: Record<SectionTone, string> = {
    page: 'var(--surface-page)',
    card: 'var(--surface-card)',
    sunken: 'var(--surface-sunken)',
    ink: 'var(--ink)',
  };
  const color = tone === 'ink' ? 'var(--text-on-inverse)' : 'var(--text-body)';
  return (
    <section id={id} style={{ background: bg[tone], color, padding: '84px 0', ...style }}>
      {children}
    </section>
  );
}

export interface EyebrowProps {
  children: React.ReactNode;
  tone?: 'brand' | 'inverse';
  style?: CSS;
}
export function Eyebrow({ children, tone = 'brand', style }: EyebrowProps) {
  const color = tone === 'inverse' ? 'var(--accent-100)' : 'var(--primary-200)';
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color, fontWeight: 500, ...style }}>
      {children}
    </span>
  );
}

export interface SectionHeadProps {
  eyebrow?: string;
  title: React.ReactNode;
  lead?: string;
  align?: 'left' | 'center';
  inverse?: boolean;
  style?: CSS;
}
export function SectionHead({ eyebrow, title, lead, align = 'left', inverse = false, style }: SectionHeadProps) {
  return (
    <div style={{ maxWidth: 720, margin: align === 'center' ? '0 auto' : 0, textAlign: align, ...style }}>
      {eyebrow && <Eyebrow tone={inverse ? 'inverse' : 'brand'}>{eyebrow}</Eyebrow>}
      <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.08, margin: eyebrow ? '14px 0 0' : 0, color: inverse ? '#fff' : 'var(--text-100)' }}>
        {title}
      </h2>
      {lead && (
        <p style={{ fontSize: 18, lineHeight: 1.6, marginTop: 16, marginBottom: 0, color: inverse ? 'rgba(255,255,255,0.78)' : 'var(--text-200)', fontWeight: 300 }}>
          {lead}
        </p>
      )}
    </div>
  );
}

// ---- Button ----
type BtnVariant = 'primary' | 'cta' | 'secondary' | 'ghost' | 'ghostInverse';
type BtnSize = 'sm' | 'md' | 'lg';
interface VariantStyle { background: string; color: string; border: string; boxShadow?: string; fontWeight?: number; }
export interface BtnProps {
  children: React.ReactNode;
  variant?: BtnVariant;
  size?: BtnSize;
  iconLeft?: string;
  iconRight?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  style?: CSS;
}
export function Btn({ children, variant = 'primary', size = 'md', iconLeft, iconRight, onClick, href, type = 'button', fullWidth = false, style }: BtnProps) {
  const sizes: Record<BtnSize, { fontSize: number; padding: string; gap: number }> = {
    sm: { fontSize: 14, padding: '9px 16px', gap: 6 },
    md: { fontSize: 16, padding: '12px 22px', gap: 8 },
    lg: { fontSize: 18, padding: '15px 30px', gap: 10 },
  };
  const variants: Record<BtnVariant, VariantStyle> = {
    primary: { background: 'var(--primary-200)', color: '#fff', border: '1px solid transparent', boxShadow: 'var(--shadow-sm)' },
    cta: { background: 'var(--accent-100)', color: 'var(--accent-200)', border: '1px solid transparent', boxShadow: 'var(--shadow-cta)', fontWeight: 700 },
    secondary: { background: 'var(--surface-card)', color: 'var(--text-100)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)' },
    ghost: { background: 'transparent', color: 'var(--primary-200)', border: '1px solid transparent' },
    ghostInverse: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' },
  };
  const s = sizes[size] ?? sizes.md;
  const v = variants[variant] ?? variants.primary;
  const commonStyle: CSS = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: s.gap,
    width: fullWidth ? '100%' : 'auto', boxSizing: 'border-box',
    fontFamily: 'var(--font-sans)', fontWeight: v.fontWeight ?? 600, fontSize: s.fontSize, lineHeight: 1,
    letterSpacing: '-0.01em', padding: s.padding, borderRadius: size === 'sm' ? 'var(--radius-sm)' : 'var(--radius-pill)',
    cursor: 'pointer', textDecoration: 'none',
    transition: 'transform var(--dur-fast) var(--ease-standard), filter var(--dur-fast) var(--ease-standard)',
    ...v, ...style,
  };
  const down = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.transform = 'scale(0.97)'; };
  const upFn = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.transform = 'scale(1)'; };
  const enter = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'brightness(0.94)'; };
  const leave = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'scale(1)'; };
  const inner = (
    <>
      {iconLeft && <Icon name={iconLeft} size={s.fontSize + 1} />}
      {children}
      {iconRight && <Icon name={iconRight} size={s.fontSize + 1} />}
    </>
  );
  if (href) {
    return (
      <a href={href} onClick={onClick} style={commonStyle} onMouseDown={down} onMouseUp={upFn} onMouseEnter={enter} onMouseLeave={leave}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} style={commonStyle} onMouseDown={down} onMouseUp={upFn} onMouseEnter={enter} onMouseLeave={leave}>
      {inner}
    </button>
  );
}

// ---- Chip / Badge ----
type ChipTone = 'info' | 'warn' | 'neutral' | 'inverse';
export interface ChipProps {
  children: React.ReactNode;
  tone?: ChipTone;
  dot?: boolean;
  style?: CSS;
}
export function Chip({ children, tone = 'info', dot = false, style }: ChipProps) {
  const tones: Record<ChipTone, { background: string; color: string; dot: string }> = {
    info: { background: 'rgba(74,144,226,0.14)', color: 'var(--chip-info-text)', dot: 'var(--primary-200)' },
    warn: { background: 'rgba(255,193,7,0.20)', color: 'var(--accent-200)', dot: 'var(--accent-100)' },
    neutral: { background: 'var(--surface-sunken)', color: 'var(--text-200)', dot: 'var(--bg-300)' },
    inverse: { background: 'rgba(255,255,255,0.14)', color: '#fff', dot: 'var(--accent-100)' },
  };
  const t = tones[tone] ?? tones.info;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '5px 11px', borderRadius: 'var(--radius-pill)', background: t.background, color: t.color, ...style }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot }} />}
      {children}
    </span>
  );
}

// ---- Panel (Card) ----
type PanelVariant = 'default' | 'flat' | 'sunken' | 'brand';
export interface PanelProps {
  children: React.ReactNode;
  variant?: PanelVariant;
  padding?: number;
  hover?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  style?: CSS;
}
export function Panel({ children, variant = 'default', padding = 28, hover = false, onClick, style }: PanelProps) {
  const variants: Record<PanelVariant, CSS> = {
    default: { background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)' },
    flat: { background: 'var(--surface-card)', border: '1px solid var(--border-default)', boxShadow: 'none' },
    sunken: { background: 'var(--surface-sunken)', border: '1px solid transparent', boxShadow: 'none' },
    brand: { background: 'var(--primary-300)', border: '1px solid transparent', boxShadow: 'var(--shadow-md)', color: 'var(--text-on-inverse)' },
  };
  const v = variants[variant] ?? variants.default;
  return (
    <div
      onClick={onClick}
      style={{ borderRadius: 'var(--radius-lg)', padding, boxSizing: 'border-box', cursor: onClick ? 'pointer' : 'default', transition: 'transform var(--dur-base) var(--ease-standard), box-shadow var(--dur-base) var(--ease-standard)', ...v, ...style }}
      onMouseEnter={(e) => { if (hover) { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; } }}
      onMouseLeave={(e) => { if (hover) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = (v.boxShadow as string) ?? 'none'; } }}
    >
      {children}
    </div>
  );
}

// ---- Icon-Tile ----
export interface IconTileProps { name: string; size?: number; tone?: 'brand' | 'amber'; }
export function IconTile({ name, size = 48, tone = 'brand' }: IconTileProps) {
  const tones: Record<'brand' | 'amber', { bg: string; fg: string }> = {
    brand: { bg: 'rgba(74,144,226,0.12)', fg: 'var(--primary-200)' },
    amber: { bg: 'rgba(255,193,7,0.16)', fg: 'var(--accent-200)' },
  };
  const t = tones[tone] ?? tones.brand;
  return (
    <span style={{ width: size, height: size, borderRadius: size >= 44 ? 14 : 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: t.bg, color: t.fg, flex: 'none' }}>
      <Icon name={name} size={Math.round(size * 0.5)} />
    </span>
  );
}

// ---- Form field ----
export interface FieldProps {
  label: string;
  hint?: string;
  type?: string;
  textarea?: boolean;
  rows?: number;
  required?: boolean;
  name: string;
  placeholder?: string;
}
export function Field({ label, hint, type = 'text', textarea = false, rows = 4, required = false, name, placeholder }: FieldProps) {
  const [focused, setFocused] = React.useState(false);
  const id = `f-${name}`;
  const base: CSS = {
    fontFamily: 'var(--font-sans)', fontSize: 16, color: 'var(--text-100)', background: 'var(--surface-page)',
    padding: '12px 14px', borderRadius: 'var(--radius-sm)', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${focused ? 'var(--primary-200)' : 'var(--border-default)'}`,
    boxShadow: focused ? 'var(--shadow-focus)' : 'none', outline: 'none', resize: textarea ? 'vertical' : 'none',
    transition: 'border-color var(--dur-fast) var(--ease-standard), box-shadow var(--dur-fast) var(--ease-standard)',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-100)' }}>
        {label}{required && <span style={{ color: 'var(--accent-200)' }}> *</span>}
      </label>
      {textarea
        ? <textarea id={id} name={name} rows={rows} placeholder={placeholder} required={required} style={base} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        : <input id={id} name={name} type={type} placeholder={placeholder} required={required} style={base} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />}
      {hint && <span style={{ fontSize: 13, color: 'var(--text-200)' }}>{hint}</span>}
    </div>
  );
}

// ---- <image-slot> React wrapper (avoids JSX intrinsic typing) ----
export interface ImageSlotProps {
  id?: string;
  shape?: string;
  src?: string;
  placeholder?: string;
  radius?: string | number;
  mask?: string;
  style?: CSS;
}
export function ImageSlot({ id, shape = 'rect', src, placeholder, radius, mask, style }: ImageSlotProps) {
  const attrs: Record<string, unknown> = { shape, style };
  if (id) attrs.id = id;
  if (src) attrs.src = src; // the custom element resolves via window.RES
  if (placeholder) attrs.placeholder = placeholder;
  if (radius !== undefined) attrs.radius = radius;
  if (mask) attrs.mask = mask;
  return React.createElement('image-slot', attrs);
}

// ---- Image frame (fixed aspect box) ----
export interface ImageFrameProps {
  id: string;
  ratio?: string;
  radius?: number;
  placeholder?: string;
  src?: string;
  style?: CSS;
}
export function ImageFrame({ id, ratio = '4/3', radius = 24, placeholder = 'Bild später einsetzen', src, style }: ImageFrameProps) {
  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: ratio, borderRadius: radius, overflow: 'hidden', boxShadow: 'var(--shadow-md)', ...style }}>
      <ImageSlot id={id} shape="rect" src={src} placeholder={placeholder} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

// ---- helpers ----
export const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
};

export const go = (path: string): void => { window.location.hash = path; };

export interface LinkProps {
  to: string;
  children: React.ReactNode;
  style?: CSS;
  className?: string;
}
export function Link({ to, children, style, className }: LinkProps) {
  return (
    <a href={`#${to}`} className={className} style={{ color: 'inherit', textDecoration: 'none', ...style }}>
      {children}
    </a>
  );
}

export { res };
